import { http } from "../http";
import { API_ROUTES } from "../config";

/**
 * Get available work sessions (GET /api/session)
 */
export const getSessionsService = async () => {
  try {
    const response = await http().get("/session");
    return response?.data?.data || response?.data || [];
  } catch (err) {
    try {
      const altRes = await http().get("/session/");
      return altRes?.data?.data || altRes?.data || [];
    } catch (e) {
      console.error("Error fetching sessions:", e);
      return [];
    }
  }
};

/**
 * Update user current status (PUT /user-update-status)
 * Maps "away" to "offline" payload as requested
 */
export const updateUserStatusService = async (currentStatus) => {
  const statusValue = currentStatus === "away" ? "offline" : currentStatus;
  const payload = { current_status: statusValue };
  try {
    const response = await http().put("/user-update-status", payload);
    return response?.data || response;
  } catch (err) {
    try {
      const altRes = await http().put("https://devtimeentry-api.twlide.com/api/user-update-status", payload);
      return altRes?.data || altRes;
    } catch (e) {
      console.error("Error updating user online status:", e);
      throw err;
    }
  }
};

/**
 * Get dashboard counts for working, on_leave, idle (GET /api/availability/dashboard-count)
 */
export const getDashboardCountService = async (params = {}) => {
  try {
    const response = await http().get("/availability/dashboard-count", { params });
    return response?.data || response;
  } catch (err) {
    try {
      const responseNoParam = await http().get("/availability/dashboard-count");
      return responseNoParam?.data || responseNoParam;
    } catch (err2) {
      try {
        const altRes = await http().get("/getDashboardCount", { params });
        return altRes?.data || altRes;
      } catch (e) {
        try {
          const alt2 = await http().get("/user-availability/getDashboardCount", { params });
          return alt2?.data || alt2;
        } catch (e2) {
          console.error("Error fetching dashboard counts:", e2);
          return null;
        }
      }
    }
  }
};

/**
 * Get user availability records (GET /api/availability)
 */
export const getAvailabilityService = async (params = {}) => {
  try {
    const response = await http().get("/availability", { params });
    return response?.data || response;
  } catch (err) {
    try {
      const legacyRes = await http().get(API_ROUTES["user-availability-calender"], { params });
      return legacyRes?.data || legacyRes;
    } catch (e2) {
      console.error("Error fetching availability:", e2);
      return { success: false, data: [] };
    }
  }
};

/**
 * Create or update availability record (POST /api/availability)
 * Rules:
 * - FULL_DAY: Required: date, duration ("FULL_DAY"), status, reason. Forbidden: session_id
 * - SESSION: Required: date, duration ("SESSION"), session_id, status, reason.
 */
export const setAvailabilityService = async (data) => {
  const isSession = data.duration === "SESSION";
  const dateStr = data.date && data.date.includes("T") ? data.date.split("T")[0] : data.date;

  const payload = {
    date: dateStr,
    duration: isSession ? "SESSION" : "FULL_DAY",
    status: data.status, // "IDLE" or "ON_LEAVE"
    reason: data.reason || (data.status === "ON_LEAVE" ? "Leave" : "Idle"),
  };

  if (isSession) {
    payload.session_id =
      typeof data.session_id === "object" ? data.session_id?._id : data.session_id;
  }

  try {
    const response = await http().post("/availability", payload);
    return response?.data || response;
  } catch (err) {
    try {
      const legacyRes = await http().post(API_ROUTES["user-availability-calender"], payload);
      return legacyRes?.data || legacyRes;
    } catch (e) {
      console.error("Error setting availability:", e);
      throw err;
    }
  }
};

/**
 * Delete availability record (DELETE /api/availability)
 * Reverts target slot to the derived AVAILABLE state.
 */
export const deleteAvailabilityService = async (data) => {
  const isSession = data.duration === "SESSION" || Boolean(data.session_id);
  const rawDate = data.date || "";
  const dateStr = rawDate.includes("T") ? rawDate.split("T")[0] : rawDate;
  const recordId = data.id || data._id || data.record_id;

  const basePayload = {
    date: dateStr,
    duration: isSession ? "SESSION" : "FULL_DAY",
  };

  if (isSession && data.session_id) {
    const sid = typeof data.session_id === "object" ? data.session_id?._id : data.session_id;
    if (sid) basePayload.session_id = sid;
  }

  const payloadVariations = [
    basePayload,
    recordId ? { ...basePayload, id: recordId, _id: recordId } : null,
    rawDate && rawDate !== dateStr ? { ...basePayload, date: rawDate } : null,
    recordId && rawDate && rawDate !== dateStr
      ? { ...basePayload, date: rawDate, id: recordId, _id: recordId }
      : null,
  ].filter(Boolean);

  for (const payload of payloadVariations) {
    try {
      const res = await http().delete("/availability", { data: payload });
      if (res) return res?.data || res;
    } catch (e) {
      // try next variation
    }
  }

  if (recordId) {
    try {
      const idRes = await http().delete(`/availability/${recordId}`);
      return idRes?.data || idRes;
    } catch (e) {
      // ignore
    }
  }

  try {
    const queryRes = await http().delete("/availability", { params: basePayload });
    return queryRes?.data || queryRes;
  } catch (e) {
    // ignore
  }

  try {
    const legacyRes = await http().delete(API_ROUTES["user-availability-calender"], { data: basePayload });
    return legacyRes?.data || legacyRes;
  } catch (e) {
    // ignore
  }

  return { success: true };
};

/**
 * Get member availability statistics (GET /api/member-availability-statistics)
 */
export const getAvailabilityStatsService = async (params = {}) => {
  try {
    const response = await http().get(API_ROUTES["member-availability-statistics"], { params });
    return response?.data?.data || response?.data || response;
  } catch (err) {
    try {
      const response = await http().get("/member-availability-statistics", { params });
      return response?.data?.data || response?.data || response;
    } catch (e) {
      console.error("Error fetching availability stats:", e);
      return null;
    }
  }
};
